// ============================================================================
// AUTHENTICATION ENDPOINTS
// ============================================================================
// Endpoint definitions for Authentication and Authorization module

import { buildEndpoint, buildEndpointWithParams } from './base.endpoints';
import type { ID } from '../types';

/**
 * Base path for auth endpoints
 */
const BASE_PATH = '/auth';

/**
 * Auth Endpoints
 */
export const authEndpoints = {
  // ============================================================================
  // TENANT HUB AUTHENTICATION (Two-Step Flow)
  // ============================================================================

  /**
   * Step 1: Discover accessible tenants for the application
   * POST /auth/discover
   */
  discover: () => buildEndpoint(`${BASE_PATH}/discover`),

  /**
   * Step 2: Authenticate with selected tenant and application
   * POST /auth/authenticate
   */
  authenticate: () => buildEndpoint(`${BASE_PATH}/authenticate`),

  /**
   * Refresh Tenant Hub token
   * POST /auth/refresh-hub
   */
  refreshHub: () => buildEndpoint(`${BASE_PATH}/refresh-hub`),

  // ============================================================================
  // LEGACY AUTHENTICATION (Backward Compatibility)
  // ============================================================================

  /**
   * User login (DEPRECATED - use discover + authenticate)
   * POST /auth/login
   */
  login: () => buildEndpoint(`${BASE_PATH}/login`),

  /**
   * User logout
   * POST /auth/logout
   */
  logout: () => buildEndpoint(`${BASE_PATH}/logout`),

  /**
   * Register new user
   * POST /auth/register
   */
  register: () => buildEndpoint(`${BASE_PATH}/register`),

  /**
   * Refresh access token
   * POST /auth/refresh
   */
  refresh: () => buildEndpoint(`${BASE_PATH}/refresh`),

  /**
   * Get current user
   * GET /auth/me
   */
  me: () => buildEndpoint(`${BASE_PATH}/me`),

  /**
   * Update current user profile
   * PUT /auth/me
   */
  updateProfile: () => buildEndpoint(`${BASE_PATH}/me`),

  /**
   * Change password
   * POST /auth/change-password
   */
  changePassword: () => buildEndpoint(`${BASE_PATH}/change-password`),

  /**
   * Forgot password
   * POST /auth/forgot-password
   */
  forgotPassword: () => buildEndpoint(`${BASE_PATH}/forgot-password`),

  /**
   * Reset password
   * POST /auth/reset-password
   */
  resetPassword: () => buildEndpoint(`${BASE_PATH}/reset-password`),

  /**
   * Verify email
   * POST /auth/verify-email
   */
  verifyEmail: () => buildEndpoint(`${BASE_PATH}/verify-email`),

  /**
   * Resend verification email
   * POST /auth/resend-verification
   */
  resendVerification: () => buildEndpoint(`${BASE_PATH}/resend-verification`),

  /**
   * Check permission
   * POST /auth/check-permission
   */
  checkPermission: () => buildEndpoint(`${BASE_PATH}/check-permission`),

  /**
   * Get user sessions
   * GET /auth/sessions
   */
  sessions: () => buildEndpoint(`${BASE_PATH}/sessions`),

  /**
   * Revoke session
   * DELETE /auth/sessions/:sessionId
   */
  revokeSession: (sessionId: string) =>
    buildEndpointWithParams(`${BASE_PATH}/sessions/:sessionId`, { sessionId }),
} as const;

/**
 * User Management Endpoints
 */
const USERS_BASE_PATH = '/users';

export const userEndpoints = {
  /**
   * Get all users
   * GET /users
   */
  list: () => buildEndpoint(USERS_BASE_PATH),

  /**
   * Get user by ID
   * GET /users/:id
   */
  getById: (id: ID) => buildEndpointWithParams(`${USERS_BASE_PATH}/:id`, { id }),

  /**
   * Create new user
   * POST /users
   */
  create: () => buildEndpoint(USERS_BASE_PATH),

  /**
   * Update user
   * PUT /users/:id
   */
  update: (id: ID) => buildEndpointWithParams(`${USERS_BASE_PATH}/:id`, { id }),

  /**
   * Delete user
   * DELETE /users/:id
   */
  delete: (id: ID) => buildEndpointWithParams(`${USERS_BASE_PATH}/:id`, { id }),

  /**
   * Search users
   * POST /users/search
   */
  search: () => buildEndpoint(`${USERS_BASE_PATH}/search`),

  /**
   * Get active users
   * GET /users/active
   */
  active: () => buildEndpoint(`${USERS_BASE_PATH}/active`),

  /**
   * Activate/Deactivate user
   * PATCH /users/:id/status
   */
  updateStatus: (id: ID) =>
    buildEndpointWithParams(`${USERS_BASE_PATH}/:id/status`, { id }),

  /**
   * Assign role to user
   * POST /users/:id/role
   */
  assignRole: (id: ID) =>
    buildEndpointWithParams(`${USERS_BASE_PATH}/:id/role`, { id }),

  /**
   * Assign permissions to user
   * POST /users/:id/permissions
   */
  assignPermissions: (id: ID) =>
    buildEndpointWithParams(`${USERS_BASE_PATH}/:id/permissions`, { id }),

  /**
   * Bulk delete users
   * DELETE /users/bulk
   */
  bulkDelete: () => buildEndpoint(`${USERS_BASE_PATH}/bulk`),
} as const;

/**
 * Export endpoints
 */
export default {
  auth: authEndpoints,
  users: userEndpoints,
};
